/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Building2, Users, Mail, Calendar, Clock, AlertCircle, Loader2, CheckCircle, XCircle, Eye, ThumbsUp, ThumbsDown, MessageSquare, RefreshCw, ArrowLeftRight } from 'lucide-react';
import MessageModal from '../components/common/MessageModal';
import { VALIDATION_MESSAGES, CDMO_IMPORT_MESSAGES } from '../constants/messages';
import {
  dbService, CDMOApplication, CdmoSafeIntroductionRequest, supabase
} from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { stripeService, StripeSubscription } from '../lib/stripe';
import MessagesTab from '../components/dashboard/MessagesTab';
import { ConversationWithDetails } from '../lib/supabase';
import { encryptId } from '../utils/encryption';
import { validateEmail, validateEmailDomainMatch, validatePhone, validateRecipientName } from '../utils/validations';
import { CDMO_OWNERSHIP_CONSTANTS } from '../constants/cdmoOwnership';
import { getPrimaryCapabilityLabel } from '../constants';

import { ChevronDown, LogOut, Edit3, Upload } from 'lucide-react';
import { CDMO_IMPORT_PATH } from '../constants/cdmoImport';

interface CDMODashboardPageProps {
  initialTab?: 'requests' | 'messages';
  validatedConversationId?: string | null;
  isVerifyingAccess?: boolean;
}

const CDMODashboardPage: React.FC<CDMODashboardPageProps> = ({
  initialTab = 'requests',
  validatedConversationId = null,
  isVerifyingAccess = false
}) => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<'requests' | 'messages'>(initialTab);
  const [cdmoApplication, setCdmoApplication] = useState<CDMOApplication | null>(null);
  const [introductionRequests, setIntroductionRequests] = useState<CdmoSafeIntroductionRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{
    requestId: string;
    newStatus: string;
    actionType: 'accept' | 'decline';
  } | null>(null);
  const [subscription, setSubscription] = useState<StripeSubscription | null>(null);
  const [subLoading, setSubLoading] = useState(true);
  const [subError, setSubError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [messagesUnreadCount, setMessagesUnreadCount] = useState(0);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(validatedConversationId);
  const [conversations, setConversations] = useState<ConversationWithDetails[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showUnclaimModal, setShowUnclaimModal] = useState(false);
  const [transferEmail, setTransferEmail] = useState('');
  const [transferName, setTransferName] = useState('');
  const [transferPhone, setTransferPhone] = useState('');
  const [isTransferring, setIsTransferring] = useState(false);
  const [transferError, setTransferError] = useState<string | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isUnclaiming, setIsUnclaiming] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [messageModal, setMessageModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'error' | 'success' | 'warning';
    onClose?: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'error'
  });

  const showMessageModal = (title: string, message: string, type: 'error' | 'success' | 'warning' = 'error', onClose?: () => void) => {
    setMessageModal({
      isOpen: true,
      title,
      message,
      type,
      onClose
    });
  };

  // Update selected conversation when validated ID changes
  useEffect(() => {
    setSelectedConversationId(validatedConversationId);

    // If we have a validated conversation ID, automatically switch to messages tab
    if (validatedConversationId) {
      console.log('🎯 ConversationGuard provided conversation ID, switching to messages tab');
      setActiveTab('messages');
    }
  }, [validatedConversationId]);

  // Deep-link support: scroll to a specific request when arriving via
  // ?requestId=<id> (email "Go to Dashboard" / "Go to your dashboard" links).
  useEffect(() => {
    const targetRequestId = searchParams.get('requestId');
    if (!targetRequestId || loading || activeTab !== 'requests') return;

    const el = document.getElementById(`intro-request-${targetRequestId}`);
    if (el) {
      el.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      el.classList.add('ring-2', 'ring-electric-blue');
      const timeout = setTimeout(() => el.classList.remove('ring-2', 'ring-electric-blue'), 3000);
      return () => clearTimeout(timeout);
    }
  }, [searchParams, loading, activeTab, introductionRequests]);

  // Update URL when tab changes
  const handleTabChange = (tab: 'requests' | 'messages') => {
    setActiveTab(tab);

    if (tab === 'messages') {
      navigate('/cdmo-dashboard/messages', { replace: true });
    } else {
      navigate('/cdmo-dashboard', { replace: true });
    }
  };

  // Update URL when conversation is selected
  const handleConversationSelect = (conversation: ConversationWithDetails) => {
    setSelectedConversationId(conversation.id);

    // If this conversation has unread messages, update the count
    if (conversation.unread_count && conversation.unread_count > 0) {
      setMessagesUnreadCount(prev => Math.max(0, prev - (conversation.unread_count || 0)));
    }

    // Update URL with encrypted conversation ID
    const encryptedId = encryptId(conversation.id);
    navigate(`/cdmo-dashboard/messages/${encryptedId}`, { replace: true });
  };

  const handleUpdateStatus = async (requestId: string, newStatus: string) => {
    const actionType = newStatus === 'accepted' ? 'accept' : 'decline';
    setConfirmAction({ requestId, newStatus, actionType });
    setShowConfirmModal(true);
  };

  const handleConfirmAction = async () => {
    if (!confirmAction) return;

    const { requestId, newStatus } = confirmAction;

    try {
      setUpdatingStatus(requestId);
      await dbService.updateIntroductionRequestStatus(requestId, newStatus);

      // Update local state
      setIntroductionRequests(prev =>
        prev.map(request =>
          request.id === requestId
            ? { ...request, status: newStatus }
            : request
        )
      );
    } catch (err: unknown) {
      console.error('Error updating status:', err);
    } finally {
      setUpdatingStatus(null);
      setShowConfirmModal(false);
      setConfirmAction(null);
    }
  };

  const handleGiveFeedback = (requestId: string) => {
    navigate(`/review/${requestId}/cdmo`);
  };

  const refreshDashboard = async () => {
    if (!user?.id) return;

    try {
      setRefreshing(true);
      const cdmoApp = await dbService.getCDMOApplicationByUserId(user.id);
      setCdmoApplication(cdmoApp);

      if (cdmoApp?.id) {
        const requests = await dbService.getCDMOIntroductionRequests(cdmoApp.id);
        setIntroductionRequests(requests);
      }

      // Fetch unread notifications count and messages count
      const [unreadCount, conversations] = await Promise.all([
        dbService.getUnreadNotificationsCount(user.id),
        dbService.getUserConversations(user.id)
      ]);
      setUnreadNotifications(unreadCount);
      setConversations(conversations);

      // Calculate total unread messages
      const totalUnread = conversations.reduce((total, conv) => total + (conv.unread_count || 0), 0);
      setMessagesUnreadCount(totalUnread);
    } catch (error) {
      console.error('Error refreshing dashboard:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const handleStartConversation = async (requestId: string) => {
    try {
      if (!user?.id) {
        console.error('User not authenticated');
        return;
      }

      // Get the introduction request details
      const request = introductionRequests.find(r => r.id === requestId);
      if (!request) {
        console.error('Request not found');
        return;
      }

      // Get or create conversation
      const conversation = await dbService.getOrCreateConversation(
        requestId,
        request.requester_user_id,
        user.id,
        cdmoApplication!.id!
      );

      // Navigate to messages tab with encrypted conversation ID
      const encryptedId = encryptId(conversation.id);
      navigate(`/cdmo-dashboard/messages/${encryptedId}`);
    } catch (error) {
      console.error('Error starting conversation:', error);
    }
  };

  // Function to handle unread count changes from MessagesTab
  const handleUnreadCountChange = (conversationId: string, newCount: number) => {
    // Update the conversations state with the new unread count
    setConversations(prev => {
      const updatedConversations = prev.map(conv =>
        conv.id === conversationId
          ? { ...conv, unread_count: newCount }
          : conv
      );

      // Recalculate total unread count from updated conversations
      const totalUnread = updatedConversations.reduce((total, conv) => total + (conv.unread_count || 0), 0);
      setMessagesUnreadCount(totalUnread);

      return updatedConversations;
    });
  };

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!user?.id) {
        setError('User not authenticated');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Check if user is admin first (gracefully handle if admin table doesn't exist)
        const { data: userData } = await supabase
          .from('users')
          .select('email')
          .eq('id', user.id)
          .single();

        let isUserAdmin = false;
        if (userData?.email) {
          try {
            isUserAdmin = await dbService.isUserAdmin(userData.email);
            setIsAdmin(isUserAdmin);
          } catch (adminError) {
            console.warn('Admin check failed (table may not exist yet):', adminError);
            setIsAdmin(false);
          }
        }

        // Try to get CDMO application (optional for admins)
        try {
          const cdmoApp = await dbService.getCDMOApplicationByUserId(user.id);
          setCdmoApplication(cdmoApp);

          if (cdmoApp?.id) {
            const requests = await dbService.getCDMOIntroductionRequests(cdmoApp.id);
            setIntroductionRequests(requests);

            // Fetch unread notifications count as well
            const unreadCount = await dbService.getUnreadNotificationsCount(user.id);
            setUnreadNotifications(unreadCount);

            // Try to enable real-time for introduction requests
            await dbService.enableRealtimeForIntroductionRequests();
          }
        } catch (cdmoError: unknown) {
          // If no CDMO application found, only show error for non-admins
          if (
            typeof cdmoError === 'object' &&
            cdmoError !== null &&
            'code' in cdmoError &&
            (cdmoError as { code: unknown }).code === 'PGRST116'
          ) {
            // PGRST116 = No rows found
            if (!isUserAdmin) {
              // Regular users need a CDMO profile
              setError('No CDMO application found. Please complete your CDMO application first.');
              setLoading(false);
              return;
            }
            // Admins can continue without a CDMO profile
            console.log('Admin accessing dashboard without CDMO profile');
          } else {
            // Other errors - show for everyone
            throw cdmoError;
          }
        }

        // Fetch unread notifications count and messages count
        if (user?.id) {
          const [unreadCount, conversations] = await Promise.all([
            dbService.getUnreadNotificationsCount(user.id),
            dbService.getUserConversations(user.id)
          ]);

          setUnreadNotifications(unreadCount);
          setConversations(conversations);

          const totalUnread = conversations.reduce((total, conv) => total + (conv.unread_count || 0), 0);
          setMessagesUnreadCount(totalUnread);
        }
      } catch (err: unknown) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load dashboard data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [user?.id]);

  // Note: Removed independent unread count subscription to prevent double-counting
  // The MessagesTab component now handles all unread count updates via onUnreadCountChange callback

  useEffect(() => {
    if (!cdmoApplication?.id) return;

    console.log('🔧 Setting up real-time subscription for CDMO:', cdmoApplication.id);

    const channel = supabase
      .channel('introduction-requests')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'introduction_requests',
          filter: `cdmo_id=eq.${cdmoApplication?.id}`,
        },
        async (payload) => {
          const requests = await dbService.getCDMOIntroductionRequests(cdmoApplication.id!);
          setIntroductionRequests(requests);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'introduction_requests',
          filter: `cdmo_id=eq.${cdmoApplication.id}`,
        },
        async (payload) => {
          const requests = await dbService.getCDMOIntroductionRequests(cdmoApplication.id!);
          setIntroductionRequests(requests);
        }
      )
      .subscribe((status) => {
        console.log('📡 Real-time subscription status:', status);
        if (status === 'SUBSCRIBED') {
          console.log('✅ Successfully subscribed to introduction requests real-time updates');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('❌ Error subscribing to real-time updates');
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [cdmoApplication?.id]);

  // Real-time subscription for message count updates (works when MessagesTab is not mounted)
  // This only updates the count, not the conversation details to prevent double-counting
  useEffect(() => {
    if (!user?.id) return;

    console.log('🔔 Setting up lightweight message count subscription for CDMO dashboard');

    const messageSubscription = supabase
      .channel(`cdmo-message-count:${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
        },
        async (payload) => {
          const newMessage = payload.new as {
            sender_id: string;
            conversation_id: string;
          };

          // Skip if this is our own message
          if (newMessage.sender_id === user.id) {
            return;
          }

          // Check if this message is from one of our conversations
          const isOurConversation = conversations.some(conv => conv.id === newMessage.conversation_id);
          if (!isOurConversation) {
            return;
          }

          console.log('📬 New message count update in CDMO dashboard:', newMessage.conversation_id);

          // Only update the total count, don't modify conversations (MessagesTab handles that)
          setMessagesUnreadCount(prev => prev + 1);
        }
      )
      .subscribe();

    return () => {
      console.log('🔕 Cleaning up CDMO message count subscription');
      messageSubscription.unsubscribe();
    };
  }, [user?.id, conversations]);

  useEffect(() => {
    const fetchSubscription = async () => {
      if (!user) {
        setSubLoading(false);
        return;
      }
      try {
        setSubError(null);
        const sub = await stripeService.getUserSubscription(user.id);
        setSubscription(sub);
      } catch (err: unknown) {
        setSubError('Failed to load subscription status');
      } finally {
        setSubLoading(false);
      }
    };
    fetchSubscription();
  }, [user]);

  const handleInitiateTransfer = async () => {
    if (!user?.id || !cdmoApplication?.id) return;
    setTransferError(null);

    // Validate email
    const emailValidation = validateEmail(transferEmail);
    if (!emailValidation.isValid) {
      setTransferError(emailValidation.error || CDMO_OWNERSHIP_CONSTANTS.INVALID_EMAIL);
      return;
    }

    if (user?.email && transferEmail?.trim().toLowerCase() === user?.email?.trim().toLowerCase()) {
      setTransferError('You cannot transfer ownership to your own email address.');
      return;
    }

    // Check if email already exists in cdmo_applications
    try {
      const { data, error } = await dbService.supabase
        .from('cdmo_applications')
        .select('id')
        .ilike('email', transferEmail.trim())
        .limit(1);

      if (error) {
        console.error('Error checking email:', error);
      } else if (data && data.length > 0) {
        setTransferError('This email is already registered in our system. Please use a different email address.');
        return;
      }
    } catch (err) {
      console.error('Error checking email exists:', err);
    }

    // Domain check
    if (cdmoApplication.website) {
      const domainMatch = validateEmailDomainMatch(transferEmail, cdmoApplication.website);
      if (!domainMatch.isValid) {
        setTransferError(CDMO_OWNERSHIP_CONSTANTS.DOMAIN_MISMATCH_ERROR);
        return;
      }
    }

    // Validate name
    const nameValidation = validateRecipientName(transferName);
    if (!nameValidation.isValid) {
      setTransferError(nameValidation.error!);
      return;
    }

    // Validate phone (full format check, same as signup page)
    const phoneValidation = validatePhone(transferPhone);
    if (!phoneValidation.isValid) {
      setTransferError(phoneValidation.error!);
      return;
    }

    try {
      setIsTransferring(true);
      // Fetch sender name from user data
      const { data: userData } = await supabase
        .from('users')
        .select('name')
        .eq('id', user.id)
        .single();
      const senderName = userData?.name || (user as any)?.user_metadata?.full_name || cdmoApplication?.contact_name || user?.email || 'Owner';

      await dbService.initiateCDMOOwnershipTransfer(
        cdmoApplication.id,
        user.id,
        user.email || '',
        senderName,
        transferEmail,
        transferName,
        transferPhone,
        cdmoApplication.company_name || ''
      );
      setShowTransferModal(false);
      showMessageModal(
        VALIDATION_MESSAGES.MODAL_TITLES.TRANSFER_INITIATED,
        CDMO_OWNERSHIP_CONSTANTS.TRANSFER_SUCCESS,
        'success'
      );
      setTransferEmail('');
      setTransferName('');
      setTransferPhone('');
    } catch (err: any) {
      console.error('Transfer error:', err);
      setTransferError(err.message || 'Failed to initiate transfer');
    } finally {
      setIsTransferring(false);
    }
  };

  const handleConfirmUnclaim = async () => {
    if (!user?.id || !cdmoApplication?.id) return;

    try {
      setIsUnclaiming(true);
      await dbService.unclaimCDMO(cdmoApplication.id, user.id);

      setShowUnclaimModal(false);
      showMessageModal(
        VALIDATION_MESSAGES.MODAL_TITLES.SUCCESS,
        VALIDATION_MESSAGES.MODAL_MESSAGES.UNCLAIM_SUCCESS_WITH_LOGIN,
        'success',
        async () => {
          await signOut();
          navigate('/', { replace: true });
        }
      );
    } catch (err: any) {
      console.error('Unclaim error:', err);
      showMessageModal(
        VALIDATION_MESSAGES.MODAL_TITLES.UNCLAIM_FAILED,
        VALIDATION_MESSAGES.MODAL_MESSAGES.UNCLAIM_FAILED_GENERIC,
        'error'
      );
    } finally {
      setIsUnclaiming(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'text-electric-blue bg-electric-blue/10 border-electric-blue/30';
      case 'accepted':
        return 'text-lime-green bg-lime-green/10 border-lime-green/30';
      case 'declined':
        return 'text-red-500 bg-red-50 border-red-200';
      default:
        return 'text-slate-gray bg-slate-gray/10 border-slate-gray/30';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-4 h-4" />;
      case 'accepted':
        return <CheckCircle className="w-4 h-4" />;
      case 'declined':
        return <XCircle className="w-4 h-4" />;
      default:
        return <Eye className="w-4 h-4" />;
    }
  };

  type RequestDetails = {
    productTypes?: string[];
    specificServices?: string[];
    requiredCertifications?: string[];
    partnerType?: string;
    developmentStage?: string;
    supportNeeds?: string[];
    batchSizeRange?: string;
    timeline?: string;
    budgetRange?: string;
    preferredRegions?: string[];
    accountCompanyStage?: string;
  };

  const formatRequestDetails = (details: RequestDetails | null | undefined) => {
    if (!details || typeof details !== 'object') return null;

    const formatArray = (arr: string[]) => arr.length > 0 ? arr.join(', ') : 'Not specified';
    const formatString = (str: string | undefined) => str || 'Not specified';

    return (
      <div className="space-y-2 text-sm">
        {details.productTypes && details.productTypes.length > 0 && (
          <div>
            <span className="font-medium text-charcoal">Product Type:</span>
            <span className="ml-2 text-slate-gray">{formatArray(details.productTypes.map(getPrimaryCapabilityLabel))}</span>
          </div>
        )}
        {details.specificServices && details.specificServices.length > 0 && (
          <div>
            <span className="font-medium text-charcoal">Services Needed:</span>
            <span className="ml-2 text-slate-gray">{formatArray(details.specificServices)}</span>
          </div>
        )}
        {details.requiredCertifications && details.requiredCertifications.length > 0 && (
          <div>
            <span className="font-medium text-charcoal">Required Certifications:</span>
            <span className="ml-2 text-slate-gray">{formatArray(details.requiredCertifications)}</span>
          </div>
        )}
        {details.partnerType && (
          <div>
            <span className="font-medium text-charcoal">CDMO Partner Type:</span>
            <span className="ml-2 text-slate-gray">{formatString(details.partnerType)}</span>
          </div>
        )}
        {details.developmentStage && (
          <div>
            <span className="font-medium text-charcoal">Development Stage:</span>
            <span className="ml-2 text-slate-gray">{formatString(details.developmentStage)}</span>
          </div>
        )}
        {details.supportNeeds && details.supportNeeds.length > 0 && (
          <div>
            <span className="font-medium text-charcoal">Support Needed:</span>
            <span className="ml-2 text-slate-gray">{formatArray(details.supportNeeds)}</span>
          </div>
        )}
        {details.batchSizeRange && (
          <div>
            <span className="font-medium text-charcoal">Batch Size:</span>
            <span className="ml-2 text-slate-gray">{formatString(details.batchSizeRange)}</span>
          </div>
        )}
        {details.timeline && (
          <div>
            <span className="font-medium text-charcoal">Timeline:</span>
            <span className="ml-2 text-slate-gray">{formatString(details.timeline)}</span>
          </div>
        )}
        {details.budgetRange && (
          <div>
            <span className="font-medium text-charcoal">Budget Range:</span>
            <span className="ml-2 text-slate-gray">{formatString(details.budgetRange)}</span>
          </div>
        )}
        {details.preferredRegions && details.preferredRegions.length > 0 && (
          <div>
            <span className="font-medium text-charcoal">Preferred Regions:</span>
            <span className="ml-2 text-slate-gray">{formatArray(details.preferredRegions)}</span>
          </div>
        )}
        {details.accountCompanyStage && (
          <div>
            <span className="font-medium text-charcoal">Company Stage:</span>
            <span className="ml-2 text-slate-gray">{formatString(details.accountCompanyStage)}</span>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-light-gray to-sky-blue/20 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl p-6 sm:p-8 text-center border border-white/50 max-w-sm w-full"
        >
          <Loader2 className="w-12 h-12 text-electric-blue mx-auto mb-4 animate-spin" />
          <h2 className="text-lg sm:text-xl font-bold text-charcoal mb-2">Loading Dashboard</h2>
          <p className="text-sm text-slate-gray">Fetching your introduction requests...</p>
        </motion.div>
      </div>
    );
  }

  if (error || subError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-light-gray to-sky-blue/20 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl p-6 sm:p-8 text-center border border-white/50 max-w-md w-full"
        >
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg sm:text-xl font-bold text-charcoal mb-2">Access Error</h2>
          <p className="text-sm text-slate-gray mb-4">{error}</p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-gradient-primary text-white font-semibold rounded-xl hover:shadow-lg transition-all duration-300"
          >
            Try Again
          </motion.button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-light-gray to-sky-blue/20">
      {/* Custom Confirmation Modal - Mobile Optimized */}
      <AnimatePresence>
        {showConfirmModal && confirmAction && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => {
              setShowConfirmModal(false);
              setConfirmAction(null);
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 20 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-6 sm:p-8 max-w-md w-full text-center border border-white/50 mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.1, type: 'spring', stiffness: 200 }}
                className={`w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6 shadow-lg ${confirmAction.actionType === 'accept'
                  ? 'bg-gradient-to-r from-lime-green to-soft-teal'
                  : 'bg-gradient-to-r from-red-500 to-red-600'
                  }`}
              >
                {confirmAction.actionType === 'accept' ? (
                  <ThumbsUp className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                ) : (
                  <ThumbsDown className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                )}
              </motion.div>

              <h3 className="text-lg sm:text-2xl font-bold text-charcoal mb-3 sm:mb-4">
                {confirmAction.actionType === 'accept' ? 'Accept Introduction?' : 'Decline Introduction?'}
              </h3>
              <p className="text-sm sm:text-base text-slate-gray mb-6 sm:mb-8 leading-relaxed">
                {confirmAction.actionType === 'accept'
                  ? 'Are you sure you want to accept this introduction request? The requester will be notified that you are interested in their project.'
                  : 'Are you sure you want to decline this introduction request? The requester will be notified that you are not available for this project.'
                }
              </p>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => {
                    setShowConfirmModal(false);
                    setConfirmAction(null);
                  }}
                  className="flex-1 px-4 sm:px-6 py-3 border border-slate-gray/30 text-slate-gray hover:bg-light-gray rounded-xl transition-all duration-300 font-medium text-sm sm:text-base"
                >
                  Cancel
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleConfirmAction}
                  disabled={updatingStatus === confirmAction.requestId}
                  className={`flex-1 px-4 sm:px-6 py-3 text-white font-semibold rounded-xl transition-all duration-300 flex items-center justify-center text-sm sm:text-base ${confirmAction.actionType === 'accept'
                    ? 'bg-gradient-to-r from-lime-green to-soft-teal hover:shadow-lg'
                    : 'bg-gradient-to-r from-red-500 to-red-600 hover:shadow-lg'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {updatingStatus === confirmAction.requestId ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      {confirmAction.actionType === 'accept' ? 'Accepting...' : 'Declining...'}
                    </>
                  ) : (
                    <>
                      {confirmAction.actionType === 'accept' ? (
                        <>
                          <ThumbsUp className="w-4 h-4 mr-2" />
                          Accept
                        </>
                      ) : (
                        <>
                          <ThumbsDown className="w-4 h-4 mr-2" />
                          Decline
                        </>
                      )}
                    </>
                  )}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header - Mobile Optimized */}
      <div className="bg-gradient-to-br from-electric-blue/20 to-deep-indigo/15 backdrop-blur-sm border-b border-electric-blue/30 relative z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-6 lg:py-8">
          {/* Main Header Content */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            {/* Left Side - Title and Company */}
            <div className="flex items-center">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-primary rounded-xl flex items-center justify-center mr-3 sm:mr-4 shadow-lg flex-shrink-0">
                <Building2 className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div className="min-w-0">
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-charcoal truncate">CDMO Dashboard</h1>
                {!isAdmin && (
                  <p className="text-sm sm:text-base text-slate-gray truncate">
                    {cdmoApplication?.company_name || 'Your Company'} - Introduction Requests
                  </p>
                )}
              </div>
            </div>

            {/* Right Side - Stats and Actions */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
              {/* Stats Row */}
              {!isAdmin && cdmoApplication && (
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center">
                    <Users className="w-4 h-4 text-electric-blue mr-2 flex-shrink-0" />
                    <span className="text-charcoal font-medium whitespace-nowrap">
                      {introductionRequests.length} Request{introductionRequests.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <button
                    onClick={refreshDashboard}
                    disabled={refreshing}
                    className="flex items-center px-3 py-2 bg-electric-blue/10 text-electric-blue rounded-lg hover:bg-electric-blue/20 transition-colors disabled:opacity-50 text-sm"
                    title="Refresh dashboard"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 flex-shrink-0 ${refreshing ? 'animate-spin' : ''}`} />
                    <span className="hidden sm:inline">Refresh</span>
                  </button>
                </div>
              )}

              {/* Action Buttons - Mobile Stack */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
                {isAdmin && (
                  <Link
                    to="/cdmo-application"
                    className="px-3 sm:px-4 py-3 bg-gradient-primary text-white rounded-lg font-semibold hover:shadow-lg transition text-center text-sm whitespace-nowrap min-h-[40px] flex items-center justify-center"
                  >
                    <span className="sm:hidden">Add New</span>
                    <span className="hidden sm:inline">Add New</span>
                  </Link>
                )}

                {isAdmin && (
                  <button
                    type="button"
                    onClick={() => navigate(CDMO_IMPORT_PATH)}
                    className="px-3 sm:px-4 py-3 bg-white text-selected-blue border border-electric-blue/40 rounded-lg font-semibold hover:bg-electric-blue/5 hover:shadow-lg transition text-center text-sm whitespace-nowrap min-h-[40px] flex items-center justify-center gap-2"
                  >
                    <Upload className="w-4 h-4" aria-hidden />
                    {CDMO_IMPORT_MESSAGES.OPEN_BUTTON}
                  </button>
                )}

                {/* Dropdown Menu for CDMO Profile Management */}
                {!isAdmin && cdmoApplication && (
                  <div
                    className="relative z-50"
                    onMouseEnter={() => setShowDropdown(true)}
                    onMouseLeave={() => setShowDropdown(false)}
                  >
                    <button
                      className="px-3 sm:px-4 py-3 bg-gradient-primary text-white rounded-lg font-semibold hover:shadow-lg transition text-center text-sm whitespace-nowrap min-h-[40px] flex items-center justify-center gap-2"
                    >
                      <Building2 className="w-4 h-4" />
                      <span>Company Profile</span>
                      <ChevronDown className={`w-4 h-4 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
                    </button>

                    <AnimatePresence>
                      {showDropdown && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-slate-gray/10 z-[60] overflow-hidden"
                        >
                          <div className="py-1">
                            <Link
                              to="/cdmo-profile-edit"
                              className="flex items-center gap-3 px-4 py-3 text-sm text-charcoal hover:bg-light-gray transition-colors"
                            >
                              <Edit3 className="w-4 h-4 text-electric-blue" />
                              Edit Company Details
                            </Link>
                            <button
                              onClick={() => {
                                setShowTransferModal(true);
                                setShowDropdown(false);
                              }}
                              className="w-full text-left flex items-center gap-3 px-4 py-3 text-sm text-charcoal hover:bg-light-gray transition-colors"
                            >
                              <ArrowLeftRight className="w-4 h-4 text-lime-green" />
                              Transfer Ownership
                            </button>
                            <button
                              onClick={() => {
                                setShowUnclaimModal(true);
                                setShowDropdown(false);
                              }}
                              className="w-full text-left flex items-center gap-3 px-4 py-3 text-sm text-red-500 hover:bg-red-50 transition-colors"
                            >
                              <LogOut className="w-4 h-4" />
                              Unclaim CDMO
                            </button>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}

                {(subscription && (subscription.status === 'active' || subscription?.status === "active_until_cancelation")) && (
                  <Link
                    to="/billing-dashboard"
                    className="px-3 sm:px-4 py-3 bg-gradient-pink-violet-end text-white rounded-lg font-semibold hover:shadow-lg transition text-center text-sm whitespace-nowrap min-h-[40px] flex items-center justify-center"
                  >
                    <span className="sm:hidden">Billing</span>
                    <span className="hidden sm:inline">Billing Dashboard</span>
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Company Overview - Mobile Optimized */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-4 sm:pt-6 lg:pt-8">
        {!isAdmin && cdmoApplication && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg border border-white/50 p-4 sm:p-6 mb-6"
          >
            <h2 className="text-lg sm:text-xl font-bold text-charcoal mb-3 sm:mb-4">Company Overview</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              <div>
                <p className="text-xs sm:text-sm text-slate-gray">Company</p>
                <p className="font-semibold text-charcoal text-sm sm:text-base truncate">{cdmoApplication?.company_name}</p>
              </div>
              <div>
                <p className="text-xs sm:text-sm text-slate-gray">Contact</p>
                <p className="font-semibold text-charcoal text-sm sm:text-base truncate">{cdmoApplication?.contact_name}</p>
              </div>
              <div className="sm:col-span-2 lg:col-span-1">
                <p className="text-xs sm:text-sm text-slate-gray">Primary Modalities</p>
                <p className="font-semibold text-charcoal text-sm sm:text-base">
                  {cdmoApplication?.primary_capabilities?.slice(0, 2).map(getPrimaryCapabilityLabel).join(', ')}
                  {cdmoApplication?.primary_capabilities && cdmoApplication?.primary_capabilities?.length > 2 &&
                    ` +${cdmoApplication?.primary_capabilities?.length - 2} more`
                  }
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Dashboard Tabs - Mobile Optimized */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="flex justify-center mb-6 sm:mb-8 px-4"
      >
        <div className="inline-flex rounded-2xl bg-white/95 backdrop-blur-sm shadow-lg border border-white/50 overflow-hidden w-full max-w-md sm:w-auto">
          {/* Requests Tab */}
          <button
            onClick={() => handleTabChange('requests')}
            className={`flex-1 sm:flex-none px-4 sm:px-6 py-2 sm:py-3 text-sm font-medium transition-all duration-300 flex items-center justify-center gap-2 ${activeTab === 'requests'
              ? 'bg-electric-blue text-white shadow-md'
              : 'text-slate-gray hover:text-charcoal hover:bg-slate-gray/5'
              }`}
          >
            <Users className="w-4 h-4 flex-shrink-0" />
            <span className="hidden sm:inline">Introduction Requests</span>
            <span className="sm:hidden">Requests</span>
            {unreadNotifications > 0 && (
              <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full min-w-[18px] flex items-center justify-center">
                {unreadNotifications > 9 ? '9+' : unreadNotifications}
              </span>
            )}
          </button>

          {/* Messages Tab */}
          <button
            onClick={() => handleTabChange('messages')}
            className={`flex-1 sm:flex-none px-4 sm:px-6 py-2 sm:py-3 text-sm font-medium transition-all duration-300 flex items-center justify-center gap-2 relative ${activeTab === 'messages'
              ? 'bg-electric-blue text-white shadow-md'
              : 'text-slate-gray hover:text-charcoal hover:bg-slate-gray/5'
              }`}
          >
            <MessageSquare className="w-4 h-4 flex-shrink-0" />
            <span>Messages</span>
            {messagesUnreadCount > 0 && (
              <span className=" right-1  bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full min-w-[18px] flex items-center justify-center">
                {messagesUnreadCount > 9 ? '9+' : messagesUnreadCount}
              </span>
            )}
          </button>
        </div>
      </motion.div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-6 sm:pb-8">
        <AnimatePresence mode="wait">
          {activeTab === 'requests' && (
            <motion.div
              key="requests"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
            >
              {/* Introduction Requests Header */}
              <div className="mb-4 sm:mb-6">
                <h2 className="text-lg sm:text-xl font-bold text-charcoal mb-2">
                  Introduction Requests
                  <span className="ml-2 text-sm font-normal text-slate-gray">
                    ({introductionRequests.length} total)
                  </span>
                </h2>
              </div>

              {introductionRequests.length === 0 ? (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg border border-white/50 p-8 sm:p-12 text-center"
                >
                  <Users className="w-12 h-12 sm:w-16 sm:h-16 text-slate-gray/50 mx-auto mb-4" />
                  <h3 className="text-lg sm:text-xl font-bold text-charcoal mb-2">No Introduction Requests Yet</h3>
                  <p className="text-sm sm:text-base text-slate-gray">
                    When biotech companies request introductions to your CDMO, they'll appear here.
                  </p>
                </motion.div>
              ) : (
                <div className="relative">
                  {!subLoading && (
                    <div className="space-y-4 sm:space-y-6">
                      {introductionRequests.map((request, index) => (
                        <motion.div
                          key={request.id}
                          id={`intro-request-${request.id}`}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3, delay: index * 0.1 }}
                          className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-lg border border-white/50 overflow-hidden transition-shadow"
                        >
                          <div className="p-4 sm:p-6">
                            {/* Request Header - Mobile Optimized */}
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
                              <div className="flex items-start space-x-3 sm:space-x-4 flex-1 min-w-0">
                                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-success rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
                                  <Users className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                                </div>
                                <div
                                  className="flex-1 min-w-0"
                                  style={subscription?.status !== 'active' && subscription?.status !== "active_until_cancelation" ? { filter: 'blur(4px)', pointerEvents: 'none', opacity: 0.6 } : {}}
                                >
                                  <h3 className="text-base sm:text-lg font-bold text-charcoal truncate">
                                    {request.sponsor_display_name || 'Anonymous Sponsor'}
                                  </h3>
                                  <div className="flex items-center text-slate-gray text-xs sm:text-sm">
                                    <Mail className="w-3 h-3 sm:w-4 sm:h-4 mr-1 flex-shrink-0" />
                                    <span className="truncate">Sponsor identity protected</span>
                                  </div>
                                </div>
                              </div>

                              {/* Status Badge */}
                              <div className="flex justify-start sm:justify-end">
                                <span className={`px-3 py-1 rounded-lg text-xs sm:text-sm font-medium border flex items-center ${getStatusColor(request.status || 'pending')}`}>
                                  {getStatusIcon(request.status || 'pending')}
                                  <span className="ml-1 capitalize">{request.status || 'pending'}</span>
                                </span>
                              </div>
                            </div>

                            {/* Date */}
                            <div className="flex items-center text-xs sm:text-sm text-slate-gray mb-4">
                              <Calendar className="w-3 h-3 sm:w-4 sm:h-4 mr-2 flex-shrink-0" />
                              <span>Request received on {new Date(request.created_at || '').toLocaleDateString()}</span>
                            </div>

                            {/* Project Requirements */}
                            <div className="bg-light-gray/50 rounded-xl p-3 sm:p-4 mb-4 sm:mb-6">
                              <h4 className="font-semibold text-charcoal mb-2 sm:mb-3 text-sm sm:text-base">Project-Fit Brief</h4>
                              {formatRequestDetails(request.shared_project_brief as RequestDetails)}
                            </div>

                            {/* Status Info and Action Buttons - Mobile Stack */}
                            <div className="space-y-4 sm:space-y-0 sm:flex sm:items-center sm:justify-between pt-4 border-t border-slate-gray/20">
                              {/* Status Info */}
                              <div className="flex items-center">
                                {request.status === 'accepted' && (
                                  <div className="flex items-center text-xs sm:text-sm text-lime-green font-medium">
                                    <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-2 flex-shrink-0" />
                                    <span>Request accepted on {new Date(request.created_at || '').toLocaleDateString()}</span>
                                  </div>
                                )}
                                {request.status === 'declined' && (
                                  <div className="flex items-center text-xs sm:text-sm text-red-500 font-medium">
                                    <XCircle className="w-3 h-3 sm:w-4 sm:h-4 mr-2 flex-shrink-0" />
                                    <span>Request declined on {new Date(request.created_at || '').toLocaleDateString()}</span>
                                  </div>
                                )}
                              </div>

                              {/* Action Buttons - Mobile Stack */}
                              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                                {request.status === 'pending' && (
                                  <>
                                    <motion.button
                                      whileHover={{ scale: 1.02 }}
                                      whileTap={{ scale: 0.98 }}
                                      onClick={() => handleUpdateStatus(request.id!, 'declined')}
                                      disabled={updatingStatus === request.id}
                                      className="px-4 py-2 border border-red-300 text-red-600 hover:bg-red-50 rounded-lg transition-all duration-300 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                                    >
                                      {updatingStatus === request.id ? (
                                        <Loader2 className="w-4 h-4 animate-spin mr-1" />
                                      ) : (
                                        <ThumbsDown className="w-4 h-4 mr-1" />
                                      )}
                                      {updatingStatus === request.id ? 'Declining...' : 'Decline'}
                                    </motion.button>
                                    <motion.button
                                      whileHover={{ scale: 1.02 }}
                                      whileTap={{ scale: 0.98 }}
                                      onClick={() => handleUpdateStatus(request.id!, 'accepted')}
                                      disabled={updatingStatus === request.id}
                                      className="px-4 sm:px-6 py-2 bg-gradient-success text-white font-semibold rounded-lg hover:shadow-lg transition-all duration-300 text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                                    >
                                      {updatingStatus === request.id ? (
                                        <Loader2 className="w-4 h-4 animate-spin mr-1" />
                                      ) : (
                                        <ThumbsUp className="w-4 h-4 mr-1" />
                                      )}
                                      {updatingStatus === request.id ? 'Accepting...' : 'Accept Introduction'}
                                    </motion.button>
                                  </>
                                )}
                                {request.status === 'accepted' && (
                                  <>
                                    <motion.button
                                      whileHover={{ scale: 1.02 }}
                                      whileTap={{ scale: 0.98 }}
                                      onClick={() => handleGiveFeedback(request.id!)}
                                      className="px-4 py-2 border border-electric-blue/30 text-electric-blue hover:bg-electric-blue/10 rounded-lg transition-all duration-300 text-sm font-medium flex items-center justify-center"
                                    >
                                      <MessageSquare className="w-4 h-4 mr-2" />
                                      Give Feedback
                                    </motion.button>
                                    <motion.button
                                      whileHover={{ scale: 1.02 }}
                                      whileTap={{ scale: 0.98 }}
                                      onClick={() => handleStartConversation(request.id!)}
                                      className="px-4 sm:px-6 py-2 bg-gradient-to-r from-electric-blue to-deep-indigo text-white font-semibold rounded-lg hover:shadow-lg transition-all duration-300 text-sm flex items-center justify-center"
                                    >
                                      <MessageSquare className="w-4 h-4 mr-2" />
                                      Start Conversation
                                    </motion.button>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}

                  {/* Subscription Overlay - Mobile Optimized */}
                  {!subLoading && (subscription == null || (subscription?.status !== 'active' && subscription.status !== "active_until_cancelation")) && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center p-4">
                      <div className="bg-white/90 rounded-xl shadow-lg p-6 sm:p-8 border border-red-200 text-center max-w-md w-full">
                        <h2 className="text-lg sm:text-xl font-bold text-red-600 mb-2">Subscription Required</h2>
                        <p className="text-sm sm:text-base text-slate-gray mb-4 font-bold">Unlock Your Leads: Subscribe to View Introduction Requests</p>
                        <a
                          href="/pricing"
                          className="inline-block px-6 py-2 bg-gradient-primary text-white rounded-lg font-semibold hover:shadow-lg transition text-sm sm:text-base"
                        >
                          View Plans
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          )}

          {activeTab === 'messages' && (
            <motion.div
              key="messages"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.3 }}
            >
              <MessagesTab
                selectedConversationId={selectedConversationId}
                onConversationSelect={handleConversationSelect}
                onUnreadCountChange={handleUnreadCountChange}
                isVerifyingAccess={isVerifyingAccess}
                totalUnreadCount={messagesUnreadCount}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      {/* Transfer Ownership Modal */}
      <AnimatePresence>
        {showTransferModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full overflow-hidden border border-white/50"
            >
              <div className="p-0">
                {/* Modal Header */}
                <div className="bg-gradient-to-r from-electric-blue/10 to-deep-indigo/10 px-6 py-6 border-b border-slate-gray/10 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-white rounded-xl shadow-md flex items-center justify-center">
                      <ArrowLeftRight className="w-6 h-6 text-electric-blue" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-charcoal">
                        {CDMO_OWNERSHIP_CONSTANTS.TRANSFER_MODAL_TITLE}
                      </h3>
                      <p className="text-sm text-slate-gray font-medium">
                        {cdmoApplication?.company_name}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowTransferModal(false)}
                    className="p-2 hover:bg-light-gray rounded-full transition-colors group"
                  >
                    <XCircle className="w-6 h-6 text-slate-gray group-hover:text-red-500 transition-colors" />
                  </button>
                </div>

                <div className="p-6 sm:p-8">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                    {/* Current Owner Section */}
                    <div className="space-y-4">
                      <h4 className="font-bold text-slate-gray uppercase text-xs tracking-wider">
                        {CDMO_OWNERSHIP_CONSTANTS.CURRENT_OWNER_SECTION}
                      </h4>
                      <div className="bg-light-gray/50 rounded-2xl p-6 space-y-4 border border-slate-gray/5">
                        <div>
                          <p className="text-xs text-slate-gray font-medium mb-1">Name</p>
                          <p className="text-charcoal font-semibold">{cdmoApplication?.contact_name || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-gray font-medium mb-1">Email</p>
                          <p className="text-charcoal font-semibold break-all">{user?.email}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-gray font-medium mb-1">Contact Phone</p>
                          <p className="text-charcoal font-semibold">{cdmoApplication?.phone || '-'}</p>
                        </div>
                      </div>
                      <p className="text-[11px] text-slate-gray italic leading-relaxed">
                        * Ownership transfer will be finalized once the recipient accepts the invitation.
                      </p>
                    </div>

                    {/* New Owner Section */}
                    <div className="space-y-4">
                      <h4 className="font-bold text-slate-gray uppercase text-xs tracking-wider">
                        {CDMO_OWNERSHIP_CONSTANTS.NEW_OWNER_SECTION}
                      </h4>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-charcoal mb-2">{CDMO_OWNERSHIP_CONSTANTS.RECIPIENT_NAME_LABEL}</label>
                          <input
                            type="text"
                            value={transferName}
                            maxLength={250}
                            onChange={(e) => setTransferName(e.target.value)}
                            className="w-full px-4 py-3 border border-slate-gray/30 rounded-xl focus:ring-2 focus:ring-electric-blue transition-all"
                            placeholder="Recipient Name"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-charcoal mb-2">Recipient Email *</label>
                          <input
                            type="email"
                            value={transferEmail}
                            onChange={(e) => setTransferEmail(e.target.value)}
                            className="w-full px-4 py-3 border border-slate-gray/30 rounded-xl focus:ring-2 focus:ring-electric-blue transition-all"
                            placeholder="newowner@company.com"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-charcoal mb-2">{CDMO_OWNERSHIP_CONSTANTS.RECIPIENT_PHONE_LABEL}</label>
                          <input
                            type="tel"
                            value={transferPhone}
                            onChange={(e) => setTransferPhone(e.target.value)}
                            className="w-full px-4 py-3 border border-slate-gray/30 rounded-xl focus:ring-2 focus:ring-electric-blue transition-all"
                            placeholder="+1 (555) 000-0000"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {transferError && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3 text-red-600 text-sm">
                      <AlertCircle className="w-5 h-5 flex-shrink-0" />
                      {transferError}
                    </div>
                  )}

                  <div className="flex gap-4">
                    <button
                      onClick={() => setShowTransferModal(false)}
                      className="flex-1 px-6 py-3 border border-slate-gray/30 text-slate-gray font-semibold rounded-xl hover:bg-light-gray transition-all"
                    >
                      {CDMO_OWNERSHIP_CONSTANTS.CANCEL_BUTTON}
                    </button>
                    <button
                      onClick={handleInitiateTransfer}
                      disabled={isTransferring || !transferEmail || !transferName || !transferPhone}
                      className="flex-1 px-6 py-3 bg-gradient-primary text-white font-bold rounded-xl hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {isTransferring ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <CheckCircle className="w-5 h-5" />
                      )}
                      {CDMO_OWNERSHIP_CONSTANTS.TRANSFER_BUTTON}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Unclaim Confirmation Modal */}
      <AnimatePresence>
        {showUnclaimModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center"
            >
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <LogOut className="w-10 h-10 text-red-600" />
              </div>
              <h3 className="text-2xl font-bold text-charcoal mb-4">
                {CDMO_OWNERSHIP_CONSTANTS.UNCLAIM_MODAL_TITLE}
              </h3>
              <p className="text-slate-gray mb-8">
                {CDMO_OWNERSHIP_CONSTANTS.UNCLAIM_CONFIRMATION}
              </p>
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => setShowUnclaimModal(false)}
                  className="w-full px-6 py-3 bg-light-gray text-charcoal font-semibold rounded-xl hover:bg-slate-gray/10 transition-all"
                >
                  {CDMO_OWNERSHIP_CONSTANTS.CANCEL_BUTTON}
                </button>
                <button
                  onClick={handleConfirmUnclaim}
                  disabled={isUnclaiming}
                  className="w-full px-6 py-3 bg-red-600 text-white font-bold rounded-xl hover:bg-red-700 transition-all flex items-center justify-center gap-2"
                >
                  {isUnclaiming ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
                  {CDMO_OWNERSHIP_CONSTANTS.UNCLAIM_BUTTON}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      {/* Message Modal */}
      <MessageModal
        isOpen={messageModal.isOpen}
        title={messageModal.title}
        message={messageModal.message}
        type={messageModal.type}
        onClose={() => {
          const onClose = messageModal.onClose;
          setMessageModal(prev => ({ ...prev, isOpen: false }));
          if (onClose) onClose();
        }}
      />
    </div>
  );
};

export default CDMODashboardPage;
